import { useState, useEffect } from 'react';

export interface RegionWeather {
  emoji: string;
  forecast: string;
  tempC: number | null;
}

export interface JohorWeather {
  tempC: number | null;
  description: string;
  emoji: string;
}

export interface SingaporeWeather {
  regions: Record<string, RegionWeather>;
  updatedAt: string | null;
}

// Representative areas for each region
const REGION_AREAS: Record<string, string[]> = {
  'North':   ['Woodlands', 'Yishun', 'Sembawang', 'Admiralty', 'Mandai'],
  'East':    ['Tampines', 'Bedok', 'Pasir Ris', 'Changi', 'Simei'],
  'South':   ['Sentosa', 'Harbourfront', 'Buona Vista', 'Queenstown', 'Tanjong Pagar'],
  'West':    ['Jurong West', 'Boon Lay', 'Clementi', 'Choa Chu Kang', 'Jurong East'],
  'Central': ['Bishan', 'Toa Payoh', 'Ang Mo Kio', 'Orchard', 'Marina Bay'],
};

function toEmoji(forecast: string): string {
  const f = forecast.toLowerCase();
  if (f.includes('thunder'))                          return '⛈️';
  if (f.includes('heavy'))                            return '🌧️';
  if (f.includes('moderate rain') || f.includes('moderate shower')) return '🌧️';
  if (f.includes('light rain') || f.includes('light shower'))       return '🌦️';
  if (f.includes('shower') || f.includes('rain'))    return '🌦️';
  if (f.includes('hazy'))                             return '🌫️';
  if (f.includes('windy'))                            return '💨';
  if (f.includes('partly cloudy') || f.includes('passing showers')) return '⛅';
  if (f.includes('cloudy'))                           return '☁️';
  if (f.includes('fair') || f.includes('sunny'))     return '☀️';
  return '🌤️';
}

function owmToEmoji(id: number): string {
  if (id >= 200 && id < 300) return '⛈️';
  if (id >= 300 && id < 400) return '🌦️';
  if (id >= 500 && id < 510) return '🌧️';
  if (id >= 510 && id < 600) return '🌦️';
  if (id >= 600 && id < 700) return '❄️';
  if (id >= 700 && id < 800) return '🌫️';
  if (id === 800) return '☀️';
  if (id === 801) return '🌤️';
  if (id === 802) return '⛅';
  if (id >= 803) return '☁️';
  return '🌤️';
}

export function useWeather(): {
  weather: SingaporeWeather | null;
  johor: JohorWeather | null;
  loading: boolean;
} {
  const [weather, setWeather] = useState<SingaporeWeather | null>(null);
  const [johor, setJohor] = useState<JohorWeather | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetch2Hour = fetch(
      'https://api.data.gov.sg/v1/environment/2-hour-weather-forecast'
    ).then(r => r.json());

    const fetchTemp = fetch(
      'https://api.data.gov.sg/v1/environment/air-temperature'
    ).then(r => r.json());

    // Johor Bahru weather via Open-Meteo (free, no API key)
    const fetchJohor = fetch(
      'https://api.open-meteo.com/v1/forecast?latitude=1.4927&longitude=103.7414&current=temperature_2m,weather_code&timezone=Asia/Singapore'
    ).then(r => r.json()).catch(() => null);

    Promise.all([fetch2Hour, fetchTemp, fetchJohor])
      .then(([forecastData, tempData, johorData]) => {
        if (cancelled) return;

        // Singapore
        const forecasts: { area: string; forecast: string }[] =
          forecastData?.items?.[0]?.forecasts ?? [];

        const readings: { value: number }[] =
          tempData?.items?.[0]?.readings ?? [];

        const avgTemp =
          readings.length > 0
            ? Math.round(
                readings.reduce((s, r) => s + r.value, 0) / readings.length
              )
            : null;

        const updatedAt: string | null =
          forecastData?.items?.[0]?.timestamp ?? null;

        const regions: Record<string, RegionWeather> = {};
        Object.entries(REGION_AREAS).forEach(([key, areas]) => {
          const match = forecasts.find(f => areas.includes(f.area));
          const fc = match?.forecast ?? 'N/A';
          regions[key] = { emoji: toEmoji(fc), forecast: fc, tempC: avgTemp };
        });

        setWeather({ regions, updatedAt });

        // Johor
        if (johorData?.current) {
          const tempC = johorData.current.temperature_2m != null
            ? Math.round(johorData.current.temperature_2m)
            : null;
          const wmoCode = johorData.current.weather_code ?? 0;
          // Map WMO code to OWM-like id for emoji
          const emojiId = wmoCode === 0 ? 800 : wmoCode <= 3 ? 801 + wmoCode - 1 :
            wmoCode <= 48 ? 741 : wmoCode <= 57 ? 301 : wmoCode <= 67 ? 501 :
            wmoCode <= 77 ? 601 : wmoCode <= 82 ? 502 : wmoCode <= 86 ? 601 :
            wmoCode >= 95 ? 200 : 800;
          setJohor({
            tempC,
            description: `WMO ${wmoCode}`,
            emoji: owmToEmoji(emojiId),
          });
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  return { weather, johor, loading };
}
